import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema, faqSchema, speakableSchema } from '../components/StructuredData';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqItems: FAQItem[] = [
    {
      question: "How many restaurants can I have per account?",
      answer: "Each TableChat account supports one restaurant location. If you operate multiple restaurants, no problem, please contact us through the contact us portal and we will assist in account establishment and table NFC arrangement across your entire organization."
    },
    {
      question: "How many users can I have per restaurant?",
      answer: "There's no limit to the number of staff members you can add to your restaurant account. You can add unlimited servers, managers, and other staff members who need access to the TableChat messaging system."
    },
    {
      question: "What is the implementation process?",
      answer: "Implementation is simple: 1) Complete your signup and payment through our secure Stripe process, 2) Receive your TableChat NFC tags and tablets, 3) Place preassigned NFC tags to tables and start receiving messages to your tablet immediately. The entire process typically takes approximately one week from signup to full operation."
    },
    {
      question: "What are the subscription terms?",
      answer: "TableChat subscriptions are provided on an annual contract basis. This commitment ensures we can deliver consistent service, support, and ongoing improvements to help your restaurant succeed. For questions about your specific contract terms, please contact our support team through the contact us portal."
    },
    {
      question: "Do my staff need training to use TableChat?",
      answer: "TableChat is designed to be simple and intuitive. Your staff only need a quick walkthrough of the Ticket Console tablet. When a guest sends a request, it appears on the console with their table number. Staff simply tap a ticket to confirm it's being handled and swipe to clear it once complete. Most teams are comfortable using it within minutes."
    },
    {
      question: "What happens if a TableChat NFC card gets damaged?",
      answer: "TableChat NFC cards are designed to be durable and long-lasting. However, if one gets damaged or destroyed, simply contact our support team to order a replacement."
    },
    {
      question: "How do customers use TableChat without downloading an app?",
      answer: "TableChat uses NFC (Near Field Communication) technology that's built into modern smartphones. Customers simply tap their phone on the TableChat card at their table, and it opens a web-based messaging interface in their browser - no app download required. It works with both iPhone and Android devices."
    },
    {
      question: "Can I customize the messages and settings for my restaurant?",
      answer: "Yes! All restaurant chats are branded with your restaurant name and logo, creating a consistent, professional, and personalized experience for your guests."
    },
    {
      question: "What if we have technical issues or need support?",
      answer: "Our system is designed to be simple to set up and easy to use, so support needs are typically minimal. That said, if you ever need help or have questions, our support team is just an email away. Visit our Contact page or use the contact form to reach us. We're happy to assist with setup and provide ongoing support to keep your TableChat system running smoothly."
    },
    {
      question: "Is the messaging system secure and private?",
      answer: "Messages are securely transmitted and processed through our system and displayed only on the restaurant's designated service tablet. During general chat, guests are identified only by table number. If a guest chooses to join the VIP program, they provide an email or phone number with verified opt-in consent — this data is stored securely and used only for the restaurant's direct marketing."
    },
    {
      question: "Can we track metrics?",
      answer: "Yes. Each location receives a daily operational report detailing all TableChat guest requests and responses from the past 24 hours, emailed to management."
    }
  ];

  return (
    <div className="faq-page">
      <SEO 
        title="FAQ - TableChat | Frequently Asked Questions"
        description="Find answers to common questions about TableChat. Learn about implementation, pricing, NFC technology, staff training, and support."
        canonical="https://tablechat.io/faq"
      />
      <StructuredData data={faqSchema(faqItems)} />
      <StructuredData data={speakableSchema('FAQ - TableChat', 'https://tablechat.io/faq', ['.faq-header', '.faq-question', '.faq-answer'])} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'FAQ', url: 'https://tablechat.io/faq' },
      ])} />
      <div className="container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about TableChat and how it can transform your restaurant's customer service.</p>
        </div>

        <div className="faq-content">
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <div 
                key={index} 
                className={`faq-item ${openItems.includes(index) ? 'active' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleItem(index)}
                  aria-expanded={openItems.includes(index)}
                >
                  <span>{item.question}</span>
                  <svg 
                    className="faq-icon" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M7 10L12 15L17 10" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="faq-contact">
            <img 
              src="/guest-messaging.png" 
              alt="Smiling server carrying a burger and fries in a busy restaurant" 
              className="faq-sidebar-image"
            />
            <div className="contact-card">
              <h3>Still have questions?</h3>
              <p>Can't find the answer you're looking for? Our support team is here to help.</p>
              <Link to="/contact" className="btn btn-primary">
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
